import Badge from '@atlaskit/badge';
import AddonIcon from '@atlaskit/icon/glyph/addon';
import BitbucketPullrequestsIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import CodeIcon from '@atlaskit/icon/glyph/code';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import DocumentFilledIcon from '@atlaskit/icon/glyph/document-filled';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import RoadmapIcon from '@atlaskit/icon/glyph/roadmap';
import UnlockFilledIcon from '@atlaskit/icon/glyph/unlock';
import UserAvatarCircleIcon from '@atlaskit/icon/glyph/user-avatar-circle';
import { MenuGroup } from '@atlaskit/menu';
import { SideNavigation } from '@atlaskit/side-navigation';
import Tooltip from '@atlaskit/tooltip';
import { ReactElement, useMemo } from 'react';
import styled from 'styled-components/macro';

import radarIcon from 'assets/images/svg/radar.svg';
import { ROUTE_PATHS } from 'settings';
import { permissionsHandler } from 'utils';

import useLeftSidebarContentHandlers from './useLeftSidebarContentHandlers';
import { NavItem } from '../navItem';

const BadgeWrapper = styled.span`
  margin-left: 0.5rem;
`;

const MenuWrapper = styled.div`
  & > div {
    overflow: none;
  }
`;

function LeftSidebarContent({ isOpen }: { isOpen: boolean }): ReactElement {
  const { unresolvedRequestsCount, resources, unresolvedOvertimeRequestsCount } =
    useLeftSidebarContentHandlers();

  const {
    canLogTime,
    canApproveTimeSheet,
    canManageLogPeriods,
    canManageIssue,
    canViewAssociateSection,
    canViewTimeOffBalance,
    canViewTimeOffManagement,
    canViewSelfMeetingNotes,
    canViewMeetingNotes,
  } = useMemo(() => permissionsHandler(resources), [resources]);

  return (
    <SideNavigation label="Left sidebar navigation">
      <MenuWrapper>
        <MenuGroup>
          {canViewTimeOffBalance ? (
            <Tooltip content={!isOpen ? 'Time Off Balance' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.TIME_OFF_BALANCE.INDEX}
                icon={<BitbucketPullrequestsIcon label="requests icon" />}
              >
                Time Off Balance
              </NavItem>
            </Tooltip>
          ) : null}

          {canLogTime || canApproveTimeSheet || canManageLogPeriods || canManageIssue ? (
            <Tooltip content={!isOpen ? 'Time Tracking' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.TIME_TRACKING.INDEX}
                icon={<EmojiFrequentIcon label="Time Tracking icon" />}
              >
                Time Tracking
              </NavItem>
            </Tooltip>
          ) : null}

          {canViewAssociateSection ? (
            <Tooltip content={!isOpen ? 'Associates' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.ASSOCIATES.INDEX}
                icon={<UserAvatarCircleIcon label="avatar icon" />}
              >
                Associates
              </NavItem>
            </Tooltip>
          ) : null}
          <Tooltip content={!isOpen ? 'Calendar' : ''} position="top">
            <NavItem to={ROUTE_PATHS.CALENDAR.INDEX} icon={<CalendarIcon label="Calendar Icon" />}>
              Calendar
            </NavItem>
          </Tooltip>
          {resources?.pending_requests?.manage_time_off_requests ? (
            <Tooltip content={!isOpen ? 'Pending Requests' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.PENDING_REQUESTS.INDEX}
                icon={<CodeIcon label="code icon" />}
              >
                Pending Requests
                {unresolvedRequestsCount ? (
                  <BadgeWrapper>
                    <Badge appearance="important">{unresolvedRequestsCount}</Badge>
                  </BadgeWrapper>
                ) : null}
              </NavItem>
            </Tooltip>
          ) : null}
          {resources?.rolesAndPermissions ? (
            <Tooltip content={!isOpen ? 'Roles And Permissions' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.ROLES_AND_PERMISSIONS.INDEX}
                icon={<UnlockFilledIcon label="unlock icon" />}
              >
                Roles And Permissions
              </NavItem>
            </Tooltip>
          ) : null}
          {resources?.time_off_report ? (
            <Tooltip content={!isOpen ? 'Time Off Report' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.TIME_OFF_REPORT.INDEX}
                icon={<RoadmapIcon label="roadmap icon" />}
              >
                Time Off Report
              </NavItem>
            </Tooltip>
          ) : null}

          {canViewTimeOffManagement ? (
            <Tooltip content={!isOpen ? 'Time Off Management' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.TIME_OFF_MANAGEMENT.INDEX}
                icon={<AddonIcon label="Addon icon" />}
              >
                Time Off Management
              </NavItem>
            </Tooltip>
          ) : null}

          {canViewMeetingNotes || canViewSelfMeetingNotes ? (
            <Tooltip content={!isOpen ? 'Notes Dashboard' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.NOTES_DASHBOARD.INDEX}
                icon={<CommentIcon label="Comment icon" />}
              >
                Notes Dashboard
              </NavItem>
            </Tooltip>
          ) : null}

          {!!resources?.surveys?.view_esat_results ||
          !!resources?.surveys?.view_list_of_all_9box_questionnaires ? (
            <Tooltip content={!isOpen ? 'Surveys' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.SURVEYS.INDEX}
                icon={<GraphBarIcon label="graph bar icon" />}
              >
                Surveys
              </NavItem>
            </Tooltip>
          ) : null}
          {process.env.REACT_APP_ENVIRONMENT &&
          ['local', 'test'].includes(process.env.REACT_APP_ENVIRONMENT) ? (
            <Tooltip content={!isOpen ? 'Compensations' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.COMPENSATIONS.INDEX}
                icon={<DocumentFilledIcon label="document icon" />}
              >
                Compensations
              </NavItem>
            </Tooltip>
          ) : null}
          {resources?.compensations ? (
            <Tooltip content={!isOpen ? 'Overtime/Undertime' : ''} position="top">
              <NavItem
                to={ROUTE_PATHS.OVERTIME_UNDERTIME.INDEX}
                icon={<RecentIcon label="overtimeUndertime icon" />}
              >
                Overtime/Undertime
                {unresolvedOvertimeRequestsCount ? (
                  <BadgeWrapper>
                    <Badge appearance="important">{unresolvedOvertimeRequestsCount}</Badge>
                  </BadgeWrapper>
                ) : null}
              </NavItem>
            </Tooltip>
          ) : null}
          {resources?.risk_radar?.view_radar ? (
            <Tooltip content={!isOpen ? 'Risk Radar' : ''} position="top">
              <NavItem to={ROUTE_PATHS.RISK_RADAR.INDEX} icon={<img src={radarIcon} alt="radar" />}>
                Risk Radar
              </NavItem>
            </Tooltip>
          ) : null}
        </MenuGroup>
      </MenuWrapper>
    </SideNavigation>
  );
}

export default LeftSidebarContent;
